import { useEffect, useRef, useState } from "react";
import { Loader2 } from "lucide-react";
import { EntryShell, entryCardClass } from "@/components/layout/EntryShell";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";
import { supabase } from "@/integrations/supabase/client";
import { useLanguage } from "@/hooks/useLanguage";
import { makeTr } from "@/lib/admin/format";
import { isTotpCode, qrImageSrc } from "@/lib/admin/mfa";

const FRIENDLY_NAME = "Zettl admin";

interface Enrollment {
  factorId: string;
  qr: string;
  secret: string;
}

/**
 * The admin's second factor: set up an authenticator app (enroll) or enter its
 * code (verify). Talks to Supabase Auth only, never to an admin function, so it
 * works on an aal1 session after verifyAdmin starts requiring aal2.
 */
export function AdminMfa({ mode, onDone }: { mode: "enroll" | "verify"; onDone: () => void }) {
  const { language } = useLanguage();
  const tr = makeTr(language);
  const [enrollment, setEnrollment] = useState<Enrollment | null>(null);
  const [factorId, setFactorId] = useState<string | null>(null);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const started = useRef(false);

  useEffect(() => {
    // React StrictMode runs effects twice in development; enroll once.
    if (started.current) return;
    started.current = true;
    void (async () => {
      setLoading(true);
      setError(null);
      const { data: factors, error: listError } = await supabase.auth.mfa.listFactors();
      if (listError) {
        setError(listError.message);
        setLoading(false);
        return;
      }
      if (mode === "verify") {
        setFactorId(factors.totp[0]?.id ?? null);
        setLoading(false);
        return;
      }
      // A setup that was started and never confirmed blocks a new one with the
      // same name: remove it first.
      for (const f of factors.all) {
        if (f.factor_type === "totp" && f.status !== "verified") {
          const { error: unenrollError } = await supabase.auth.mfa.unenroll({ factorId: f.id });
          if (unenrollError) console.warn("unenroll unverified factor:", unenrollError.message);
        }
      }
      const { data, error: enrollError } = await supabase.auth.mfa.enroll({
        factorType: "totp",
        friendlyName: FRIENDLY_NAME,
        issuer: "Zettl",
      });
      if (enrollError || !data) {
        setError(enrollError?.message ?? "enroll failed");
      } else {
        setEnrollment({ factorId: data.id, qr: data.totp.qr_code, secret: data.totp.secret });
        setFactorId(data.id);
      }
      setLoading(false);
    })();
  }, [mode]);

  const submit = async () => {
    if (!factorId || !isTotpCode(code)) return;
    setBusy(true);
    setError(null);
    const { error: verifyError } = await supabase.auth.mfa.challengeAndVerify({ factorId, code });
    setBusy(false);
    if (verifyError) {
      setCode("");
      setError(
        tr(
          "Kód nesouhlasí nebo vypršel. Zkuste nový.",
          "The code didn't match or expired. Try a new one."
        )
      );
      return;
    }
    onDone();
  };

  return (
    <EntryShell>
      <Card className={entryCardClass}>
        <CardHeader>
          <CardTitle className="text-xl">
            {mode === "enroll"
              ? tr("Nastavte ověřovací aplikaci", "Set up an authenticator app")
              : tr("Ověření dvěma kroky", "Two-step verification")}
          </CardTitle>
          <CardDescription>
            {mode === "enroll"
              ? tr(
                  "Administrace vyžaduje druhý krok přihlášení. Naskenujte QR kód aplikací (Google Authenticator, 1Password, Authy…) a zadejte kód, který ukáže.",
                  "The admin console needs a second sign-in step. Scan the QR code with an authenticator app (Google Authenticator, 1Password, Authy…) and enter the code it shows."
                )
              : tr(
                  "Zadejte šestimístný kód z ověřovací aplikace.",
                  "Enter the 6-digit code from your authenticator app."
                )}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-5">
          {loading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : (
            <>
              {mode === "enroll" && enrollment && (
                <div className="space-y-3">
                  <div className="flex justify-center">
                    {/* White tile: QR codes must stay dark on light to scan, also in dark mode. */}
                    <img
                      src={qrImageSrc(enrollment.qr)}
                      alt={tr("QR kód pro ověřovací aplikaci", "QR code for the authenticator app")}
                      className="h-48 w-48 rounded-lg bg-white p-2"
                    />
                  </div>
                  <div className="space-y-1 text-center">
                    <p className="text-xs text-muted-foreground">
                      {tr("Nebo zadejte klíč ručně:", "Or enter the key by hand:")}
                    </p>
                    <code className="block break-all rounded-md bg-muted px-3 py-2 font-mono text-sm tracking-wider">
                      {enrollment.secret}
                    </code>
                  </div>
                </div>
              )}
              {factorId && (
                <form
                  className="flex flex-col items-center gap-4"
                  onSubmit={(e) => {
                    e.preventDefault();
                    void submit();
                  }}
                >
                  <InputOTP
                    maxLength={6}
                    value={code}
                    onChange={setCode}
                    autoFocus
                    inputMode="numeric"
                    pattern="^[0-9]*$"
                    aria-label={tr("Kód z aplikace", "Code from the app")}
                  >
                    <InputOTPGroup>
                      {Array.from({ length: 6 }, (_, i) => (
                        <InputOTPSlot key={i} index={i} />
                      ))}
                    </InputOTPGroup>
                  </InputOTP>
                  <Button type="submit" className="w-full" disabled={busy || !isTotpCode(code)}>
                    {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                    {mode === "enroll"
                      ? tr("Ověřit a zapnout", "Verify and turn on")
                      : tr("Ověřit", "Verify")}
                  </Button>
                </form>
              )}
              {error && (
                <p className="text-center text-sm text-destructive" role="alert">
                  {error}
                </p>
              )}
              {!factorId && !error && (
                <p className="text-center text-sm text-muted-foreground">
                  {tr(
                    "Ověřovací aplikaci se nepodařilo načíst.",
                    "Could not load the authenticator."
                  )}
                </p>
              )}
            </>
          )}
          <a
            href={`/${language}/dashboard`}
            className="block text-center text-[13px] text-muted-foreground hover:text-foreground"
          >
            {tr("Zpět do aplikace", "Back to the app")}
          </a>
        </CardContent>
      </Card>
    </EntryShell>
  );
}
